import React, { FC, useCallback } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  createInfiniteSearchSelect,
  Form,
  FormAlerts,
  FormField,
} from '../../../common/form';
import { Padding } from '../../../common/components/Padding';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { confirmationHeaders } from '../../../common/utils';
import { useMutationErrors } from '../../../common/queryClient';
import { ActionButton } from '../../../common/components';
import translations from './UserFollowedDataSubjectModal.translations.json';
import { UserFollowedDataSubjectValidFormDataType } from './schema/types';
import { UserFollowedDataSubjectSchema } from './schema/UserFollowedDataSubjectSchema';
import { useMutateUserFollowedDataSubjectQuery } from './query/useMutateUserFollowedDataSubjectQuery';
import {
  DataSubjectOption,
  useInfiniteSearchDataSubjectsQuery,
} from '../../../datasubject/query/useDataSubjectsQuery';
import { useFormContext } from 'react-hook-form';

const { addTranslations, t } = i18nNsFactory(
  'user-followed-data-subject-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface UserFollowedDataSubjectModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const UserFollowedDataSubjectModal: FC<
  UserFollowedDataSubjectModalProps
> = (props) => {
  const { onClose, onSuccess } = props;

  const { mutateAsync } = useMutateUserFollowedDataSubjectQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSubmit = useCallback(
    (values: UserFollowedDataSubjectValidFormDataType, confirm: boolean) => {
      clearMutationErrors();
      return mutateAsync({
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(onSuccess)
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [clearMutationErrors, mutateAsync, onSuccess, setMutationErrors],
  );
  return (
    <Form
      schema={UserFollowedDataSubjectSchema.validFormDataSchema}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="userFollowedDataSubject"
    >
      <UserFollowedDataSubjectModalInner onClose={onClose} />
    </Form>
  );
};

export const FormDataSubjectsAutoComplete = createInfiniteSearchSelect(
  useInfiniteSearchDataSubjectsQuery,
);

function UserFollowedDataSubjectModalInner({
  onClose,
}: {
  onClose: () => void;
}) {
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const { setValue } = useFormContext();
  const handleChange = useCallback(
    (value: any, option: DataSubjectOption | DataSubjectOption[]) => {
      const selectedOption = Array.isArray(option) ? option[0] : option;
      if (selectedOption?.item) {
        const item = selectedOption.item;
        const opts = { shouldDirty: false };
        setValue('dataSubjectId', item.id ?? undefined, opts);
      }
    },
    [setValue],
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      minWidth="30rem"
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <Flex>
            <FormField
              name="dataSubjectCode"
              label={t('dataSubjectId.label')}
              size="small"
              required
            >
              <FormDataSubjectsAutoComplete
                name="dataSubjectCode"
                style={{ width: '25rem' }}
                styles={{
                  popup: { root: { width: '300px' } },
                }}
                onChange={handleChange}
              />
            </FormField>
          </Flex>
        </Flex>
      </Padding>
    </Modal>
  );
}
